/**
 * Lightfield Import Wizard
 *
 * Three-step fullscreen wizard for importing data from Lightfield:
 *   1. Connect — paste + validate an API key
 *   2. Configure — choose which object types to import
 *   3. Review — review + override the engine's proposed property mappings
 *
 * Wizard chrome (modal shell, step indicator, footer, dirty-close
 * confirmation) is provided by the base `Wizard` component. On successful
 * create, notifies the parent with the returned import_id so it can hand
 * off to the reused CSV `ReviewModal`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Wizard, WizardPollingBody, type WizardStep } from '@/lib/base/wizard';
import type { CsvImportStatus } from '@/lib/csv-import/api';

import {
  type CreateImportResult,
  describeConfirmError,
  describeLightfieldError,
  type DiscoveredObjectType,
  discoverLightfieldTypes,
  type LightfieldConfirmedMapping,
  type LightfieldJobStatus,
  type LightfieldMappingAction,
  type LightfieldMappingProposal,
  type MappingProposalsResult,
  startLightfieldImport,
  startLightfieldMappingProposals,
  validateLightfieldKey,
  waitForLightfieldJob,
} from './api';
import { LIGHTFIELD_OBJECT_TYPES } from './constants';
import { LightfieldConfigureStep } from './LightfieldConfigureStep';
import { LightfieldConnectStep } from './LightfieldConnectStep';
import { LightfieldReviewStep, reviewRowKey } from './LightfieldReviewStep';

// ============================================================================
// Types
// ============================================================================

interface LightfieldImportWizardProps {
  orgId: number;
  isOpen: boolean;
  onClose: () => void;
  onImportCreated: (importId: number, status: CsvImportStatus) => void;
}

interface LightfieldImportWizardState {
  apiKey: string;
  connectError: string | null;
  selectedTypes: string[];
  discoveredTypes: DiscoveredObjectType[];
  proposalsByEntity: Record<string, LightfieldMappingProposal[]> | null;
  proposalsLoadError: boolean;
  truncatedByEntity: Record<string, boolean> | null;
  totalRecordsByEntity: Record<string, number> | null;
  actionByKey: Record<string, LightfieldMappingAction>;
  jobStatus: LightfieldJobStatus | null;
}

const STEP_CONNECT = 'connect';
const STEP_CONFIGURE = 'configure';
const STEP_REVIEW = 'review';

/**
 * Loading copy for a dispatched job, keyed on the status the poll reports.
 * `pending` means no worker has claimed the job yet, so naming the fetch would
 * be a lie — the honest word is that it is queued. Hoisted so the label
 * functions in `steps` stay referentially stable.
 */
function describeJobProgress(
  status: LightfieldJobStatus | null,
  runningLabel: string
): string {
  return status === 'pending' ? 'Queued' : runningLabel;
}

// Default configure-step selection: every supported object type. Hoisted so
// the initial state, reset, and the isDirty comparison all share one stable
// reference.
const DEFAULT_SELECTED_TYPES = LIGHTFIELD_OBJECT_TYPES.map(type => type.name);

// The built-in (non-custom) object types. A selection in this set is always
// valid regardless of discovery; anything else is a discovered custom
// (`org_...`) type that only stays valid while it's in the current discovery
// result.
const BUILTIN_TYPE_NAMES = new Set(DEFAULT_SELECTED_TYPES);

/**
 * Reconcile a prior selection against a freshly-discovered custom-type set.
 * Built-in selections are always kept. A custom (`org_...`) selection is kept
 * only if it's still in `discoveredTypes` — so a re-run that drops a type, or a
 * discovery failure (empty set), removes stale custom selections that would
 * otherwise submit `org_...` names against the wrong Lightfield schema.
 */
function reconcileSelectedTypes(
  prevSelected: string[],
  discoveredTypes: DiscoveredObjectType[]
): string[] {
  const discoveredNames = new Set(
    discoveredTypes.map(type => type.entity_type_name)
  );
  return prevSelected.filter(
    name => BUILTIN_TYPE_NAMES.has(name) || discoveredNames.has(name)
  );
}

// ============================================================================
// Main component
// ============================================================================

export function LightfieldImportWizard({
  orgId,
  isOpen,
  onClose,
  onImportCreated,
}: LightfieldImportWizardProps) {
  const [apiKey, setApiKey] = useState('');
  const [connectError, setConnectError] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    () => DEFAULT_SELECTED_TYPES
  );
  const [discoveredTypes, setDiscoveredTypes] = useState<
    DiscoveredObjectType[]
  >([]);
  const [proposalsByEntity, setProposalsByEntity] = useState<Record<
    string,
    LightfieldMappingProposal[]
  > | null>(null);
  const [proposalsLoadError, setProposalsLoadError] = useState(false);
  const [truncatedByEntity, setTruncatedByEntity] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [totalRecordsByEntity, setTotalRecordsByEntity] = useState<Record<
    string,
    number
  > | null>(null);
  const [actionByKey, setActionByKey] = useState<
    Record<string, LightfieldMappingAction>
  >({});
  const [jobStatus, setJobStatus] = useState<LightfieldJobStatus | null>(null);

  // Keep a ref current during each render so render delegates / canAdvance /
  // onBeforeNext closures read the latest state without capturing it in
  // their dep arrays — prevents the steps useMemo from recomputing on every
  // keystroke.
  const stateRef = useRef<LightfieldImportWizardState>({
    apiKey,
    connectError,
    selectedTypes,
    discoveredTypes,
    proposalsByEntity,
    proposalsLoadError,
    truncatedByEntity,
    totalRecordsByEntity,
    actionByKey,
    jobStatus,
  });
  stateRef.current = {
    apiKey,
    connectError,
    selectedTypes,
    discoveredTypes,
    proposalsByEntity,
    proposalsLoadError,
    truncatedByEntity,
    totalRecordsByEntity,
    actionByKey,
    jobStatus,
  };

  // Tracks the active AbortController so the cleanup effect can abort on
  // unmount, stopping any in-progress polling loop.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  const handleApiKeyChange = useCallback((value: string) => {
    setApiKey(value);
    setConnectError(null);
  }, []);

  const handleTypesChange = useCallback((names: string[]) => {
    setSelectedTypes(names);
  }, []);

  const reset = useCallback(() => {
    setApiKey('');
    setConnectError(null);
    setSelectedTypes(DEFAULT_SELECTED_TYPES);
    setDiscoveredTypes([]);
    setProposalsByEntity(null);
    setProposalsLoadError(false);
    setTruncatedByEntity(null);
    setTotalRecordsByEntity(null);
    setActionByKey({});
    setJobStatus(null);
  }, []);

  // Stable across renders so passing it into waitForLightfieldJob never
  // re-triggers anything downstream.
  const handleJobStatus = useCallback((status: LightfieldJobStatus) => {
    setJobStatus(status);
  }, []);

  // The wizard has unsaved input once a key is typed or the object-type
  // selection diverges from the default (all types). Drives the base Wizard's
  // dirty-close confirmation so in-progress input isn't silently discarded.
  const isDirty = useMemo(() => {
    const selectionChanged =
      selectedTypes.length !== DEFAULT_SELECTED_TYPES.length ||
      DEFAULT_SELECTED_TYPES.some(name => !selectedTypes.includes(name));
    return apiKey.trim() !== '' || selectionChanged;
  }, [apiKey, selectedTypes]);

  // Only place state is reset — clears the pasted key on close.
  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  // Abort the in-flight poll and leave the wizard. During a poll every footer
  // CTA and the header close button are disabled (the wizard holds
  // isSubmitting), so the polling status view's escape hatch is the user's
  // only exit. There is no server-side cancellation for a dispatched job —
  // aborting only stops observing the poll; the backend job still completes.
  const handleCancelPoll = useCallback(() => {
    controllerRef.current?.abort();
    handleClose();
  }, [handleClose]);

  // ---------------------------------------------------------------------------
  // Step render delegates
  // ---------------------------------------------------------------------------

  const renderConnect = useCallback(
    () => (
      <LightfieldConnectStep
        apiKey={stateRef.current.apiKey}
        errorMessage={stateRef.current.connectError}
        onApiKeyChange={handleApiKeyChange}
      />
    ),
    [handleApiKeyChange]
  );

  const renderConfigure = useCallback(
    () => (
      <WizardPollingBody
        title='Analyzing your Lightfield data…'
        description='We’re mapping your Lightfield fields to Ahoy properties. This can take a few minutes for a large workspace.'
        onCancel={handleCancelPoll}
      >
        <LightfieldConfigureStep
          selected={stateRef.current.selectedTypes}
          onChange={handleTypesChange}
          discoveredTypes={stateRef.current.discoveredTypes}
        />
      </WizardPollingBody>
    ),
    [handleTypesChange, handleCancelPoll]
  );

  const handleActionChange = useCallback(
    (key: string, action: LightfieldMappingAction) => {
      setActionByKey(prev => ({ ...prev, [key]: action }));
    },
    []
  );

  const renderReview = useCallback(
    () => (
      <WizardPollingBody
        title='Starting your import…'
        description='We’re fetching your Lightfield records and creating the import. This can take a few minutes for a large workspace.'
        onCancel={handleCancelPoll}
      >
        <LightfieldReviewStep
          proposalsByEntity={stateRef.current.proposalsByEntity}
          loadError={stateRef.current.proposalsLoadError}
          truncatedByEntity={stateRef.current.truncatedByEntity}
          totalRecordsByEntity={stateRef.current.totalRecordsByEntity}
          actionByKey={stateRef.current.actionByKey}
          onActionChange={handleActionChange}
        />
      </WizardPollingBody>
    ),
    [handleActionChange, handleCancelPoll]
  );

  // ---------------------------------------------------------------------------
  // Per-step onBeforeNext / wizard-level confirm
  // ---------------------------------------------------------------------------

  const handleConnectBeforeNext = useCallback(async () => {
    setConnectError(null);
    try {
      const res = await validateLightfieldKey(orgId, stateRef.current.apiKey);
      if (!res.valid) {
        setConnectError('That API key is invalid or expired.');
        throw new Error('invalid-key');
      }
    } catch (err) {
      // The invalid-key sentinel already set a specific message above; for
      // every other failure surface one so the user isn't silently stuck on
      // the Connect step. describeLightfieldError covers both ApiError (e.g.
      // the 502 network copy) and non-ApiError rejections like offline /
      // request timeout (falling back to a generic message).
      if (!(err instanceof Error && err.message === 'invalid-key')) {
        setConnectError(describeLightfieldError(err));
      }
      throw err;
    }

    // Discovery is best-effort: a failure here must not block advancing to
    // Configure. Built-in object types still import fine without it, so we
    // just proceed with an empty list — the user never sees an error for
    // this.
    //
    // Whatever discovery returns (or fails to), reconcile the current
    // selection against it: drop any selected custom (`org_...`) type that's
    // no longer in the newly-discovered set — and all of them on failure
    // (empty set) — so a stale selection can't submit an `org_...` name against
    // a schema the user can no longer see. Built-in selections are preserved.
    let discoveredTypes: DiscoveredObjectType[];
    try {
      const discoverRes = await discoverLightfieldTypes(
        orgId,
        stateRef.current.apiKey
      );
      discoveredTypes = discoverRes.custom_object_types;
    } catch {
      discoveredTypes = [];
    }
    setDiscoveredTypes(discoveredTypes);
    setSelectedTypes(prev => reconcileSelectedTypes(prev, discoveredTypes));
  }, [orgId]);

  // Fetches the engine's proposed property mappings when leaving Configure,
  // pre-selecting each row to the engine's proposed_action. Never rethrows —
  // if the request fails we still advance to Review (with the load-error
  // notice) rather than leaving the user stuck on Configure.
  const handleConfigureBeforeNext = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setProposalsLoadError(false);
    setJobStatus(null);
    try {
      // Dispatch + poll. Awaiting inside onBeforeNext keeps the wizard's own
      // isSubmitting spinner on the button for the whole wait, so the async
      // boundary needs no extra UI — and unlike the old inline call, a real
      // org's fetch is no longer racing a 30s request timeout.
      const { job_id } = await startLightfieldMappingProposals(orgId, {
        apiKey: stateRef.current.apiKey,
        selectedObjectTypes: stateRef.current.selectedTypes,
      });
      const job = await waitForLightfieldJob(
        orgId,
        job_id,
        controller.signal,
        handleJobStatus
      );
      const res = job.result as unknown as MappingProposalsResult;
      const initial: Record<string, LightfieldMappingAction> = {};
      for (const proposals of Object.values(res.proposals)) {
        for (const proposal of proposals) {
          initial[reviewRowKey(proposal.entity_type_name, proposal.raw_key)] =
            proposal.proposed_action;
        }
      }
      setProposalsByEntity(res.proposals);
      setTruncatedByEntity(res.truncated);
      setTotalRecordsByEntity(res.total_records);
      setActionByKey(initial);
    } catch (err) {
      controller.abort();
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled via the polling status view's escape hatch — the
        // wizard is already closing and its state reset by handleClose. Skip
        // the load-error notice (setting it here would survive the reset and
        // wrongly show on the next open). Rethrow so the wizard's onBeforeNext
        // rejection path keeps us on Configure rather than advancing to Review
        // as the wizard tears down.
        throw err;
      }
      setProposalsByEntity(null);
      setTruncatedByEntity(null);
      setTotalRecordsByEntity(null);
      setActionByKey({});
      setProposalsLoadError(true);
    } finally {
      // The job is terminal either way; leaving the last non-terminal status
      // set would make the Review step's CTA open on stale progress copy.
      setJobStatus(null);
    }
  }, [orgId, handleJobStatus]);

  // Returning the promise engages the wizard's isSubmitting double-submit
  // guard, preventing duplicate imports while the (synchronous, can take
  // many seconds) backend create call is in flight. On failure we do NOT
  // reset — apiKey/selectedTypes survive so the user can retry without
  // re-pasting.
  const handleConfirm = useCallback(async () => {
    const controller = new AbortController();
    controllerRef.current = controller;
    try {
      const { proposalsByEntity, proposalsLoadError, actionByKey } =
        stateRef.current;
      const confirmedMappings: LightfieldConfirmedMapping[] | undefined =
        proposalsLoadError ||
        !proposalsByEntity ||
        Object.keys(proposalsByEntity).length === 0
          ? undefined
          : Object.values(proposalsByEntity)
              .flat()
              .map(proposal => ({
                raw_key: proposal.raw_key,
                entity_type_name: proposal.entity_type_name,
                action:
                  actionByKey[
                    reviewRowKey(proposal.entity_type_name, proposal.raw_key)
                  ] ?? proposal.proposed_action,
                target_property_name: proposal.target_property_name,
                field_type_name: proposal.field_type_name,
                is_multi: proposal.is_multi ?? false,
              }));

      const { job_id } = await startLightfieldImport(orgId, {
        apiKey: stateRef.current.apiKey,
        selectedObjectTypes: stateRef.current.selectedTypes,
        ...(confirmedMappings ? { confirmedMappings } : {}),
      });
      const job = await waitForLightfieldJob(
        orgId,
        job_id,
        controller.signal,
        handleJobStatus
      );
      const res = job.result as unknown as CreateImportResult;
      if (res?.warning) {
        toast.warning(res.warning);
      }
      if (job.csv_import_id === null) {
        // Succeeded without an import id should be impossible — the create task
        // sets it in the same write that marks the job succeeded. Fail loudly
        // rather than handing the CSV modal a null and rendering an empty shell.
        throw new Error('Lightfield import finished without an import id');
      }
      onImportCreated(job.csv_import_id, res.status);
      handleClose();
    } catch (err) {
      controller.abort();
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User cancelled via the polling status view's escape hatch during the
        // create poll — the wizard is already closing and its state reset by
        // handleClose. A deliberate abort, not a failure: skip the error toast.
        return;
      }
      toast.error(describeConfirmError(err));
      throw err;
    } finally {
      // On failure the wizard stays open for a retry, so the last non-terminal
      // status must not persist onto the re-enabled CTA.
      setJobStatus(null);
    }
  }, [orgId, onImportCreated, handleClose, handleJobStatus]);

  // ---------------------------------------------------------------------------
  // Step config
  // ---------------------------------------------------------------------------

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        id: STEP_CONNECT,
        label: 'Connect',
        title: 'Connect Lightfield',
        render: renderConnect,
        canAdvance: () => stateRef.current.apiKey.trim() !== '',
        onBeforeNext: handleConnectBeforeNext,
        primaryCta: {
          label: ctx => (ctx.isSubmitting ? 'Verifying' : 'Continue'),
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: STEP_CONFIGURE,
        label: 'Configure',
        title: 'Choose what to import',
        render: renderConfigure,
        canAdvance: () => stateRef.current.selectedTypes.length > 0,
        onBeforeNext: handleConfigureBeforeNext,
        primaryCta: {
          label: ctx =>
            ctx.isSubmitting
              ? describeJobProgress(
                  stateRef.current.jobStatus,
                  'Reading your Lightfield data'
                )
              : 'Continue',
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
      },
      {
        id: STEP_REVIEW,
        label: 'Review',
        title: 'Review properties',
        render: renderReview,
        canAdvance: () => true,
        primaryCta: {
          label: ctx =>
            ctx.isSubmitting
              ? describeJobProgress(
                  stateRef.current.jobStatus,
                  'Starting import'
                )
              : 'Start import',
        },
        secondaryCtas: [
          {
            id: 'cancel',
            label: 'Cancel',
            onClick: ({ close }) => close(),
          },
        ],
        maxContentWidth: 900,
      },
    ],
    [
      renderConnect,
      renderConfigure,
      renderReview,
      handleConnectBeforeNext,
      handleConfigureBeforeNext,
    ]
  );

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      steps={steps}
      onConfirm={handleConfirm}
      isDirty={isDirty}
      dialogAriaLabel='Import from Lightfield'
      instance='lightfield-import'
    />
  );
}

export default LightfieldImportWizard;
